/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { useReactiveVar } from '@apollo/client';
import * as React from 'react';
import { useApprovalsAppSelectors } from '..';

import {
    availableFlowEnvironments,
    flowEnvironmentsLoading,
} from '../../../apollo/reactiveVariables/FlowEnvironmentVariables';
import { Constants } from '../../../common/Constants';
import { useSetFilterDataMutation } from '../../../data/client/mutations/SetFilterData.generated';
import { useSetListDataMutation } from '../../../data/client/mutations/SetListData.generated';
import { useSetSidebarDataMutation } from '../../../data/client/mutations/SetSidebarData.generated';
import { useFilterDataQuery } from '../../../data/client/queries/FilterData.generated';
import { useListDataQuery } from '../../../data/client/queries/ListData.generated';
import { useSidebarDataQuery } from '../../../data/client/queries/SidebarData.generated';
import { ApprovalRequestType, ListData } from '../../../data/types.generated';
import { LogCategory } from '../../../logger/LogCategories';
import { LegacyMobileApprovalsAppCommandBarComponent } from '../../../mobile/components/MobileApprovalsCommandBar/LegacyMobileApprovalsAppCommandBar.component';
import { MobileApprovalsAppCommandBarComponent } from '../../../mobile/components/MobileApprovalsCommandBar/MobileApprovalsAppCommandBar.component';
import { ListEnvironmentsClient, TeamsEnvironment } from '../../../service/gen';
import { newGuid } from '../../../utilities/AppUtils';
import { getFlowEnvironmentIdFromSubEntityId } from '../../../utilities/SubEntityUtils';
import { getAppHost } from '../../../utilities/TeamsUtils';
import { useDataExportButton } from '../../containers/DataExport/DataExportModal';
import { useShowESignLogoutButton } from '../../Esign/utils/Authentication/LogoutUtils';
import {
    ApprovalsAppCommandBarComponent,
    ApprovalsAppCommandBarComponentProps,
} from './ApprovalsAppCommandBar.component';
import { LegacyApprovalsAppCommandBarComponent } from './LegacyApprovalsAppCommandBar.component';

export const ApprovalsAppCommandBarContainer: React.FC = (): JSX.Element => {
    const host = getAppHost();
    const logger = host.getLogger();
    const logCategory = LogCategory.PersonalAppCommandBar;
    const isAdditionalFiltersEnabled = getAppHost().getEcsConfig().MicrosoftTeamApprovals.AdditionalFilters;
    const clientRequestGUID = React.useRef(newGuid());

    React.useEffect((): void => {
        flowEnvironmentsLoading(true);
        new ListEnvironmentsClient()
            .getTeamsEnvironments(clientRequestGUID.current)
            .then((result: TeamsEnvironment[]): void => {
                logger.logTraceInfo(logCategory, `Found ${result.length} environments for the user`);
                availableFlowEnvironments(result);
                flowEnvironmentsLoading(false);
            })
            .catch((error: any): void => {
                logger.logTraceHttpException('ListEnvironments', error.message, error.status);
                logger.logTraceWarning(
                    logCategory,
                    'Failed to fetch environments, will probably only work with default environment now!'
                );
            });
    }, [clientRequestGUID]);

    const [setSidebarDataMutation] = useSetSidebarDataMutation();
    const [setFilterDataMutation] = useSetFilterDataMutation();
    const [setListDataMutation] = useSetListDataMutation();
    const { data: sidebarData } = useSidebarDataQuery();
    const {
        data: { listData },
    } = useListDataQuery();
    const { data: filterData } = useFilterDataQuery();

    const showESignLogoutButton = useShowESignLogoutButton(listData.approvalType);

    const isSidebarExpanded = sidebarData.sidebarData?.isExpanded;
    const isSidebarFloating = sidebarData.sidebarData?.isFloating;
    const onSidebarToggleClick = React.useCallback((): void => {
        const inTabletBoundary = window.innerWidth <= Constants.TABLET_UPPER_BOUNDARY;
        logger.logTraceInfo(logCategory, `Toggled side bar expand/collapse, in tablet boundary? ${inTabletBoundary}`);
        setSidebarDataMutation({
            variables: {
                isExpanded: inTabletBoundary ? isSidebarExpanded : !isSidebarExpanded,
                isFloating: inTabletBoundary ? !isSidebarFloating : isSidebarFloating,
            },
        });
    }, [isSidebarExpanded, isSidebarFloating, setSidebarDataMutation, logCategory, logger]);

    const isSidebarOpen = isSidebarExpanded || isSidebarFloating;
    const isFilterOpen = filterData.filterData?.isOpen;

    const onFilterToggleClick = React.useCallback((): void => {
        logger.logTraceInfo(logCategory, 'Toggled filter from the app command bar');

        setFilterDataMutation({
            variables: {
                isOpen: !filterData.filterData.isOpen,
            },
        });
    }, [setFilterDataMutation, filterData, listData]);

    const setListData = React.useCallback(
        (listData: Partial<ListData>): void => {
            logger.logTraceInfo(logCategory, 'Refreshing list data');
            setListDataMutation({
                variables: listData,
            });
        },
        [setListDataMutation]
    );

    const skipSelectors =
        listData?.approvalType === ApprovalRequestType.Basic ||
        listData?.approvalType === ApprovalRequestType.ESign ||
        listData?.approvalType === ApprovalRequestType.Docusign;
    const selectors = useApprovalsAppSelectors(listData, skipSelectors);

    const subEntityId = host.getSubEntityId();
    React.useEffect((): void => {
        const flowEnvironmentId = getFlowEnvironmentIdFromSubEntityId(subEntityId);
        if (flowEnvironmentId) {
            setListDataMutation({
                variables: { flowEnvironment: flowEnvironmentId },
            });
        }
    }, [subEntityId, setListDataMutation]);

    const environmentList = useReactiveVar(availableFlowEnvironments);

    const dataExportButton = useDataExportButton(listData);

    const props: ApprovalsAppCommandBarComponentProps = {
        environmentList,
        isFilterOpen,
        isSidebarOpen,
        listData,
        selectors,
        dataExportButton,
        onSidebarToggleClick,
        onFilterToggleClick,
        setListData,
        showESignLogoutButton,
    };

    return host.isMobile() ? (
        isAdditionalFiltersEnabled ? (
            <MobileApprovalsAppCommandBarComponent {...props} />
        ) : (
            <LegacyMobileApprovalsAppCommandBarComponent {...props} />
        )
    ) : isAdditionalFiltersEnabled ? (
        <ApprovalsAppCommandBarComponent {...props} />
    ) : (
        <LegacyApprovalsAppCommandBarComponent {...props} />
    );
};
